import { useState } from 'react';

import { ChevronLeft, ChevronRight, Loader2, RotateCcw } from 'lucide-react';

import { DailyTrendChart } from '@/components/dashboard/DailyTrendChart';
import { GenericKpiCards } from '@/components/dashboard/GenericKpiCards';
import { HorizontalBarChart } from '@/components/dashboard/HorizontalBarChart';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useAvailabilityDashboard, useAvailabilityRecent } from '@/hooks/useAnalytics';
import { usePageTitle } from '@/hooks/usePageTitle';
import { type GlobalFilters } from '@/lib/api/analytics.api';
import { cn } from '@/lib/utils';
import { formatDateTime } from '@/utils/format';

import type { Direction } from '@repo/shared-types';

const DIRECTIONS: { value: Direction; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'INBOUND', label: 'Inbound' },
  { value: 'OUTBOUND', label: 'Outbound' },
];

const SHIFT_TYPE_COLORS: Record<string, string> = {
  day: 'bg-amber-500',
  night: 'bg-indigo-500',
  morning: 'bg-teal-500',
  afternoon: 'bg-sky-500',
  evening: 'bg-rose-500',
};

const SHIFT_TYPE_LABELS: Record<string, string> = {
  day: 'Day',
  night: 'Night',
  morning: 'Morning',
  afternoon: 'Afternoon',
  evening: 'Evening',
};

export const AvailabilityDashboardPage = () => {
  usePageTitle('Availability Dashboard');
  const [direction, setDirection] = useState<Direction>('ALL');
  const [executedAtFrom, setExecutedAtFrom] = useState('');
  const [executedAtTo, setExecutedAtTo] = useState('');
  const [page, setPage] = useState(1);
  const [showAllCampaigns, setShowAllCampaigns] = useState(false);

  const globalFilters: GlobalFilters = {
    direction,
    ...(executedAtFrom ? { executedAtFrom } : {}),
    ...(executedAtTo ? { executedAtTo } : {}),
    timePreset: 'all',
  };

  const { data, isLoading, error } = useAvailabilityDashboard(globalFilters);
  const {
    data: recentData,
    isLoading: recentLoading,
    error: recentError,
  } = useAvailabilityRecent(globalFilters, page);

  if (isLoading && !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <Loader2 className="text-muted-foreground h-8 w-8 animate-spin" />
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="flex h-full items-center justify-center">
        <p className="text-destructive text-sm">Failed to load availability dashboard data.</p>
      </div>
    );
  }

  if (!data) return null;

  const kpiItems = [
    {
      title: 'Total Calls',
      value: data.kpi.totalCaptured.toLocaleString(),
      subtext: 'Calls with availability captured',
    },
    {
      title: 'Capture Rate',
      value: `${data.kpi.captureRate}%`,
      subtext: `${data.kpi.totalCaptured.toLocaleString()} of ${data.kpi.answeredCalls.toLocaleString()} answered`,
    },
    {
      title: 'Total Slots',
      value: data.kpi.totalSlots.toLocaleString(),
      subtext: `${data.kpi.avgSlotsPerCall} avg per call`,
    },
    {
      title: 'Consent Rate',
      value: `${data.kpi.consentRate}%`,
      subtext: `${data.kpi.consentedCount.toLocaleString()} consented captures`,
    },
    {
      title: 'Avg Slots / Call',
      value: data.kpi.avgSlotsPerCall.toLocaleString(),
      subtext: `${data.kpi.totalSlots.toLocaleString()} slots across ${data.kpi.totalCaptured.toLocaleString()} calls`,
    },
  ];

  const shiftTypeItems = data.shiftTypeBreakdown.map((item) => ({
    label: SHIFT_TYPE_LABELS[item.shiftType] ?? item.shiftType,
    value: item.count,
    color: SHIFT_TYPE_COLORS[item.shiftType],
  }));

  const directionItems = [
    { label: 'Inbound', value: data.directionSplit.inbound, color: 'bg-blue-500' },
    { label: 'Outbound', value: data.directionSplit.outbound, color: 'bg-violet-500' },
  ];

  const consentItems = [
    { label: 'Consented', value: data.consentBreakdown.consented, color: 'bg-emerald-500' },
    { label: 'Declined', value: data.consentBreakdown.declined, color: 'bg-rose-500' },
    { label: 'Not Obtained', value: data.consentBreakdown.notObtained, color: 'bg-slate-400' },
  ];

  const allCampaignItems = data.campaignBreakdown.map((item, i) => ({
    label: item.campaignName,
    value: item.count,
    color: [
      'bg-blue-500',
      'bg-teal-500',
      'bg-amber-500',
      'bg-rose-500',
      'bg-indigo-500',
      'bg-emerald-500',
      'bg-orange-500',
      'bg-cyan-500',
    ][i % 8],
  }));
  const campaignItems = showAllCampaigns ? allCampaignItems : allCampaignItems.slice(0, 5);

  const pagination = recentData?.pagination;

  const formatSlotRange = (earliest: string | null, latest: string | null) => {
    if (!earliest) return '-';
    const fmt = (d: string) => {
      const dt = new Date(`${d}T00:00:00`);
      return dt.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
    };
    if (earliest === latest) return fmt(earliest);
    return `${fmt(earliest)} - ${fmt(latest!)}`;
  };

  return (
    <div className="space-y-6 p-6">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="text-foreground text-lg font-semibold">Availability Dashboard</h1>
        <span
          className="text-muted-foreground border-muted-foreground/30 rounded-full border px-2 py-0.5 text-[11px] font-medium"
          title={
            data.sources.availability === 'tool'
              ? 'Availability figures count only what was uploaded to the booking system. Consent comes from the review of the conversation, because it is asked after the availability is saved.'
              : 'Every figure on this page comes from the review of what was said on the conversation.'
          }
        >
          {data.sources.availability === 'tool' ? 'Uploaded · consent reviewed' : 'Reviewed'}
        </span>

        <div className="bg-muted h-6 w-px" />

        <div className="flex items-center gap-1 rounded-lg border p-[3px]">
          {DIRECTIONS.map((d) => (
            <button
              key={d.value}
              type="button"
              onClick={() => {
                setDirection(d.value);
                setPage(1);
              }}
              className={cn(
                'rounded-md px-3 py-1 text-xs font-medium transition-all',
                direction === d.value
                  ? 'bg-primary text-primary-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
            >
              {d.label}
            </button>
          ))}
        </div>

        <div className="hidden items-center gap-2 sm:flex">
          <div className="bg-muted h-6 w-px" />
          <label className="text-muted-foreground text-xs font-medium">From</label>
          <input
            type="date"
            value={executedAtFrom}
            onChange={(e) => {
              setExecutedAtFrom(e.target.value);
              setPage(1);
            }}
            className="focus:ring-primary bg-background text-foreground h-7 rounded-md border px-2 text-xs focus:ring-1 focus:outline-none dark:border-slate-700"
          />
          <span className="text-muted-foreground text-xs">&rarr;</span>
          <input
            type="date"
            value={executedAtTo}
            onChange={(e) => {
              setExecutedAtTo(e.target.value);
              setPage(1);
            }}
            className="focus:ring-primary bg-background text-foreground h-7 rounded-md border px-2 text-xs focus:ring-1 focus:outline-none dark:border-slate-700"
          />
        </div>

        <button
          type="button"
          title="Reset all filters"
          onClick={() => {
            setDirection('ALL');
            setExecutedAtFrom('');
            setExecutedAtTo('');
            setPage(1);
          }}
          className="text-muted-foreground hover:text-foreground flex items-center gap-1 rounded-md px-2 py-1 text-xs transition-colors hover:bg-white/5"
        >
          <RotateCcw className="h-3 w-3" />
          Reset
        </button>
      </div>

      {/* KPI Cards */}
      <GenericKpiCards items={kpiItems} />

      {/* Daily Trend — full width */}
      <DailyTrendChart
        title="Daily Captures"
        data={data.dailyTrend}
        emptyMessage="No captures for the selected period."
      />

      {/* Charts Row 1 */}
      <div className="grid gap-6">
        <HorizontalBarChart
          title="Shift Type Breakdown"
          items={shiftTypeItems}
          emptyMessage="No availability slots for the selected period."
        />
      </div>

      {/* Charts Row 2 */}
      <div className="grid gap-6 lg:grid-cols-2">
        <HorizontalBarChart
          title="Inbound vs Outbound"
          items={directionItems}
          emptyMessage="No availability captures for the selected period."
        />
        <HorizontalBarChart
          title="Consent Breakdown (reviewed)"
          items={consentItems}
          emptyMessage="No availability captures for the selected period."
        />
      </div>

      {/* Campaign Breakdown — full width */}
      <div>
        <HorizontalBarChart
          title={`Captures by Campaign${!showAllCampaigns && allCampaignItems.length > 5 ? ' (Top 5)' : ''}`}
          items={campaignItems}
          emptyMessage="No campaign data for the selected period."
        />
        {allCampaignItems.length > 5 && (
          <div className="mt-2 flex justify-center">
            <button
              type="button"
              onClick={() => setShowAllCampaigns((v) => !v)}
              className="text-primary hover:text-primary/80 text-sm font-medium transition-colors"
            >
              {showAllCampaigns ? 'Show Top 5' : `Show All (${allCampaignItems.length})`}
            </button>
          </div>
        )}
      </div>

      {/* Recent Captures Table */}
      <Card>
        <CardHeader>
          <CardTitle>Recent Captures</CardTitle>
        </CardHeader>
        <CardContent>
          {recentLoading && !recentData ? (
            <div className="flex justify-center py-8">
              <Loader2 className="text-muted-foreground h-6 w-6 animate-spin" />
            </div>
          ) : recentError ? (
            <p className="text-destructive py-8 text-center text-sm">
              Failed to load recent captures. Please try again.
            </p>
          ) : recentData && recentData.data.length > 0 ? (
            <>
              <div className="overflow-x-auto">
                <table className="w-full border-collapse text-sm">
                  <thead>
                    <tr className="text-muted-foreground text-left text-xs tracking-wide uppercase">
                      <th className="border-b px-3 py-2">Phone</th>
                      <th className="border-b px-3 py-2">Contact Name</th>
                      <th className="border-b px-3 py-2">Slots</th>
                      <th className="border-b px-3 py-2">Slot Dates</th>
                      <th className="border-b px-3 py-2">Shift Types</th>
                      <th className="border-b px-3 py-2">Confirmed</th>
                      <th className="border-b px-3 py-2">Consented</th>
                      <th className="border-b px-3 py-2">Direction</th>
                      <th className="border-b px-3 py-2">Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentData.data.map((row) => (
                      <tr key={row.id} className="hover:bg-muted/50 border-b transition-colors">
                        <td className="px-3 py-2 font-mono text-sm">{row.phone_number ?? '-'}</td>
                        <td className="px-3 py-2">{row.contact_name ?? '-'}</td>
                        <td className="px-3 py-2">{row.slots_count}</td>
                        <td className="text-muted-foreground px-3 py-2 text-xs">
                          {formatSlotRange(row.earliest_slot_date, row.latest_slot_date)}
                        </td>
                        <td className="px-3 py-2">
                          <div className="flex flex-wrap gap-1">
                            {row.shift_types.map((st) => (
                              <span
                                key={st}
                                className="rounded-full bg-slate-100 px-2 py-0.5 text-xs font-medium dark:bg-slate-800"
                              >
                                {SHIFT_TYPE_LABELS[st] ?? st}
                              </span>
                            ))}
                          </div>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={cn(
                              'rounded-full px-2 py-0.5 text-xs font-medium',
                              row.confirmed
                                ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400'
                                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
                            )}
                          >
                            {row.confirmed ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-3 py-2">
                          <span
                            className={cn(
                              'rounded-full px-2 py-0.5 text-xs font-medium',
                              row.consented
                                ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-400'
                                : 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-400',
                            )}
                          >
                            {row.consented ? 'Yes' : 'No'}
                          </span>
                        </td>
                        <td className="px-3 py-2 text-xs">{row.direction}</td>
                        <td className="text-muted-foreground px-3 py-2 text-xs">
                          {formatDateTime(row.executed_at)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              {pagination && pagination.totalPages > 1 && (
                <div className="mt-4 flex items-center justify-between">
                  <p className="text-muted-foreground text-sm">
                    Page {pagination.page} of {pagination.totalPages} ({pagination.total} total)
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page <= 1}
                      onClick={() => setPage((p) => p - 1)}
                    >
                      <ChevronLeft className="mr-1 h-4 w-4" />
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page >= pagination.totalPages}
                      onClick={() => setPage((p) => p + 1)}
                    >
                      Next
                      <ChevronRight className="ml-1 h-4 w-4" />
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <p className="text-muted-foreground py-8 text-center text-sm">
              No availability captures for the selected period.
            </p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};
